import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import i18n from "i18next";
import { Snackbar, Alert } from "@mui/material";
import { LuX, LuCheck, LuChevronDown, LuPlay, LuShapes } from "react-icons/lu";
import VideoThumb from "../../timeline/IdeaTimeline/components/VideoThumb";
import { animationDisplayName } from "../../timeline/IdeaTimeline/components/AnimationPanel";
import { FILTER_PRESETS } from "../../right_panel/ToolsPanel";
import {
  clearStyleClipboard,
  getStyleClipboard,
  subscribeStyleClipboard,
} from "../../../utils/styleClipboard";
import "./_canvasOverlay.scss";

const t = (key, opts) => i18n.t(`styleClipboard.${key}`, opts);

// i18n.t returns the key itself when a translation is missing.
function label(key, fallback) {
  const v = i18n.t(key);
  return v && v !== key ? v : fallback;
}

const alignLabel = (v) => label(`styleClipboard.align_${v}`, v);
const borderStyleLabel = (v) => label(`styles.border_style_options.${v}`, v);
const fontLabel = (v) => String(v).split(",")[0].trim().replace(/["']/g, "");
function presetLabel(key) {
  const preset = FILTER_PRESETS.find((p) => p.key === key);
  return preset ? label(preset.labelKey, key) : key;
}

const TRANSPARENT = /^rgba\(\d+,\d+,\d+,0\)$|^transparent$/i;

function isTransparent(color) {
  return !color || TRANSPARENT.test(String(color).replace(/\s/g, ""));
}

function summarize(payload) {
  const parts = [t(`kind_${payload.sourceKind}`)];
  if (payload.style && Object.keys(payload.style).length) parts.push(t("chip_box"));
  if (payload.hasImageAdjust) parts.push(t("chip_image"));
  if (payload.hasTransform) parts.push(t("chip_pose"));
  if (payload.entryAnimation || payload.exitAnimation) parts.push(t("chip_animation"));
  return parts.join(" · ");
}

function sameFour(a, b, c, d) {
  return a === b && b === c && c === d;
}

function px(value) {
  return value === undefined || value === null || value === "" ? "0px" : `${value}px`;
}

function Muted({ children }) {
  return <span className="styleClipboardDetails__muted">{children}</span>;
}

function Color({ value }) {
  if (isTransparent(value)) return <Muted>{t("value_transparent")}</Muted>;
  return (
    <span className="styleClipboardDetails__color">
      <i style={{ background: value }} />
      {value}
    </span>
  );
}

function boxRows(style) {
  const s = style || {};
  const radii = [
    s.borderTopLeftRadius,
    s.borderTopRightRadius,
    s.borderBottomRightRadius,
    s.borderBottomLeftRadius,
  ];
  const pads = [s.paddingTop, s.paddingRight, s.paddingBottom, s.paddingLeft];
  const fourSided = (values) =>
    !values.some((v) => Number(v) > 0) ? (
      <Muted>{t("value_none")}</Muted>
    ) : sameFour(...values) ? (
      px(values[0])
    ) : (
      values.map(px).join(" · ")
    );
  return [
    [t("row_background"), <Color key="bg" value={s.backgroundColor} />],
    [
      t("row_border"),
      Number(s.borderWidth) > 0 ? (
        <span className="styleClipboardDetails__color">
          <i style={{ background: s.borderColor }} />
          {`${px(s.borderWidth)} ${borderStyleLabel(s.borderStyle || "solid")}`}
        </span>
      ) : (
        <Muted>{t("value_none")}</Muted>
      ),
    ],
    [t("row_radius"), fourSided(radii)],
    [t("row_padding"), fourSided(pads)],
    [
      t("row_opacity"),
      s.opacity == null || Number(s.opacity) === 1 ? (
        <Muted>100%</Muted>
      ) : (
        `${Math.round(Number(s.opacity) * 100)}%`
      ),
    ],
  ];
}

function textRows(text) {
  const f = text || {};
  const marks = [
    ["B", f.bold, { fontWeight: 700 }],
    ["I", f.italic, { fontStyle: "italic" }],
    ["U", f.underline, { textDecoration: "underline" }],
  ].filter(([, on]) => on);
  return [
    [
      t("row_font"),
      f.textFontFamily ? (
        <span style={{ fontFamily: f.textFontFamily }}>{fontLabel(f.textFontFamily)}</span>
      ) : (
        <Muted>{t("value_default")}</Muted>
      ),
    ],
    [t("row_size"), f.textFontSize ? `${f.textFontSize}px` : <Muted>{t("value_default")}</Muted>],
    [
      t("row_weight"),
      marks.length ? (
        <span className="styleClipboardDetails__marks">
          {marks.map(([label, , style]) => (
            <b key={label} style={style}>
              {label}
            </b>
          ))}
        </span>
      ) : (
        <Muted>{t("value_default")}</Muted>
      ),
    ],
    [t("row_align"), f.textAlign ? alignLabel(f.textAlign) : <Muted>{t("value_default")}</Muted>],
    [
      t("row_color"),
      f.textColor ? <Color value={f.textColor} /> : <Muted>{t("value_default")}</Muted>,
    ],
    [
      t("row_highlight"),
      f.highlightColor && !isTransparent(f.highlightColor) ? (
        <Color value={f.highlightColor} />
      ) : (
        <Muted>{t("value_none")}</Muted>
      ),
    ],
  ];
}

function poseRows(transform) {
  const p = transform || {};
  const flips = [p.horizontalFlip && t("value_flip_h"), p.verticalFlip && t("value_flip_v")].filter(
    Boolean
  );
  return [
    [t("row_rotation"), Number(p.rotation) ? `${p.rotation}°` : <Muted>0°</Muted>],
    [t("row_flip"), flips.length ? flips.join(" · ") : <Muted>{t("value_none")}</Muted>],
  ];
}

function imageRows(adjust) {
  const a = adjust || {};
  return [
    [
      t("row_preset"),
      a.filterPreset && a.filterPreset !== "none" ? (
        presetLabel(a.filterPreset)
      ) : (
        <Muted>{t("value_none")}</Muted>
      ),
    ],
    [t("row_intensity"), a.filterIntensity == null ? <Muted>100%</Muted> : `${a.filterIntensity}%`],
  ];
}

function animationLabel(anim) {
  if (!anim || !anim.animation) return <Muted>{t("value_none")}</Muted>;
  const name = anim.animation.name || anim.animation;
  const bits = [animationDisplayName(String(name)), `${anim.duration ?? 1}s`];
  if (Number(anim.delay) > 0) bits.push(`${anim.delay}s ${t("value_delay")}`);
  return bits.join(" · ");
}

function Group({ title, rows }) {
  return (
    <section className="styleClipboardDetails__group">
      <h5>{title}</h5>
      <dl>
        {rows.map(([label, value]) => (
          <div key={label} className="styleClipboardDetails__row">
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function SourcePreview({ payload }) {
  const s = payload.style || {};
  const frame = {
    borderStyle: s.borderStyle || "solid",
    borderWidth: Math.min(3, Number(s.borderWidth) || 0) + "px",
    borderColor: isTransparent(s.borderColor) ? "transparent" : s.borderColor,
    borderRadius: Math.min(10, Number(s.borderTopLeftRadius ?? s.borderRadius) || 0) + "px",
    opacity: s.opacity != null ? Math.max(0.25, Number(s.opacity)) : 1,
  };
  const fill = isTransparent(s.backgroundColor) ? "transparent" : s.backgroundColor;

  if (payload.sourceKind === "image" && payload.sourceUrl) {
    return (
      <span className="styleClipboardCard__swatch styleClipboardCard__swatch--media" style={frame}>
        <img src={payload.sourceUrl} alt="" />
      </span>
    );
  }
  if (payload.sourceKind === "video" && payload.sourceUrl) {
    return (
      <span className="styleClipboardCard__swatch styleClipboardCard__swatch--media" style={frame}>
        <VideoThumb url={payload.sourceUrl} className="styleClipboardCard__video" />
        <i className="styleClipboardCard__play">
          <LuPlay size={9} />
        </i>
      </span>
    );
  }
  if (payload.sourceKind === "text") {
    const f = payload.textFormat || {};
    return (
      <span
        className="styleClipboardCard__swatch"
        style={{
          ...frame,
          background: fill,
          color: f.textColor || "#334155",
          fontFamily: f.textFontFamily || "inherit",
          fontWeight: f.bold ? 700 : 600,
          fontStyle: f.italic ? "italic" : "normal",
          textDecoration: f.underline ? "underline" : "none",
        }}
      >
        Aa
      </span>
    );
  }
  return (
    <span
      className="styleClipboardCard__swatch"
      style={{ ...frame, background: fill, color: "#64748b" }}
    >
      <LuShapes size={16} />
    </span>
  );
}

const StyleClipboardBar = ({ selectedIds, onApply }) => {
  const [payload, setPayload] = useState(getStyleClipboard);
  const [toast, setToast] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(
    () =>
      subscribeStyleClipboard((next) => {
        setPayload(next);
        setOpen(false);
        if (next) setToast({ severity: "success", message: t("copied") });
      }),
    []
  );

  if (!payload) return null;

  const targets = selectedIds || [];

  const apply = () => {
    const result = onApply(payload, targets) || { applied: 0, skipped: 0 };
    setToast({
      severity: result.skipped ? "warning" : "success",
      message: result.skipped
        ? t("applied_skipped", { n: result.applied, skipped: result.skipped })
        : t("applied", { n: result.applied }),
    });
  };

  return (
    <>
      <div className={"styleClipboardCard" + (open ? " is-open" : "")}>
        <div className="styleClipboardCard__bar">
          <span className="styleClipboardCard__swatchWrap">
            <SourcePreview payload={payload} />
          </span>
          <span className="styleClipboardCard__info">
            <span className="styleClipboardCard__title">{t("title")}</span>
            <span className="styleClipboardCard__meta">
              {targets.length ? summarize(payload) : t("hint")}
            </span>
          </span>
          <button
            type="button"
            className="styleClipboardCard__apply"
            disabled={!targets.length}
            onClick={apply}
          >
            <LuCheck size={15} />
            {t("apply")}
            {targets.length ? <b>{targets.length}</b> : null}
          </button>
          <button
            type="button"
            className={"styleClipboardCard__toggle" + (open ? " is-open" : "")}
            title={open ? t("details_hide") : t("details")}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            <LuChevronDown size={15} />
          </button>
          <span className="styleClipboardCard__divider" />
          <button
            type="button"
            className="styleClipboardCard__close"
            title={t("clear")}
            onClick={() => clearStyleClipboard()}
          >
            <LuX size={15} />
          </button>
        </div>

        {open ? (
          <div className="styleClipboardDetails">
            <Group title={t("group_box")} rows={boxRows(payload.style)} />
            {payload.textFormat ? (
              <Group title={t("group_text")} rows={textRows(payload.textFormat)} />
            ) : null}
            {payload.hasImageAdjust ? (
              <Group title={t("group_image")} rows={imageRows(payload.imageAdjust)} />
            ) : null}
            <Group title={t("group_pose")} rows={poseRows(payload.transform)} />
            <Group
              title={t("group_animation")}
              rows={[
                [t("row_entry"), animationLabel(payload.entryAnimation)],
                [t("row_exit"), animationLabel(payload.exitAnimation)],
              ]}
            />
          </div>
        ) : null}
      </div>
      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={toast ? toast.severity : "success"} onClose={() => setToast(null)}>
          {toast ? toast.message : ""}
        </Alert>
      </Snackbar>
    </>
  );
};

StyleClipboardBar.propTypes = {
  selectedIds: PropTypes.array,
  onApply: PropTypes.func.isRequired,
};

export default StyleClipboardBar;
